// These imports need to happen before any component.
// tailwind.css contains tailwind's normalize which will override
// component's behaviour if imported after them. It's imported as its own
// top-level statement (not via globals.scss's `@import`) so Next never
// routes it through sass-loader as a nested dependency — that reinvocation
// compiles it in CSS syntax mode, which can't resolve the SCSS partials
// injected by packages/styling/scss's prependData (CEXP-22728).
import 'styles/tailwind.css';
import 'styles/globals.scss';
// Every @anchorage/common component stylesheet, in one file so it holds a fixed
// cascade position: after Tailwind's preflight and utilities, before this app's
// own CSS modules.
import '@anchorage/common/dist/components/all.css';
// Import the NonceProvider leaf, NOT the SelectV2 barrel: the barrel pulls
// react-select-v5 + the full _elements graph synchronously into _app.js
// (Phase 1, CEXP-20078). Regressed once via a stale-branch merge (#192086) —
// keep this on the deep path.
// eslint-disable-next-line no-restricted-imports
import { NonceProvider } from '@anchorage/common/dist/components/SelectV2/NonceProvider';
import { LazyChunkErrorBoundary } from '@anchorage/error-boundary/lazy-chunk';
import FadingTransition from '@anchorage/frontoffice/components/FadingTransition';
import {
  ACCESS_DENIED_PATH,
  DASHBOARD_LOGIN_PATH,
} from '@anchorage/frontoffice/utils/routes';
import useIsPortoApp from '@anchorage/frontoffice/utils/useIsPortoApp';
import { LocalStorageProvider, usePortoTheme } from '@anchorage/hooks';
import { ClientReadyGate, useFontSetup } from '@anchorage/next';
import { handleError } from '@anchorage/sentry';
import { configureSpriteUrl } from '@latitude/icons';
import { Toaster, ToasterProvider } from '@latitude/toast';
import { TooltipProvider } from '@latitude/tooltip';
import { SchemaMismatchBanner } from 'components/SchemaMismatchBanner';
import { setNonce } from 'get-nonce';
import { useReportUninterpolatedNavigation } from 'hooks/useReportUninterpolatedNavigation';
import NextAdapterPages from 'next-query-params/pages';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { Inter, Roboto_Mono } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { NuqsAdapter } from 'nuqs/adapters/next/pages';
import { useEffect, useMemo, useState } from 'react';
import { QueryParamProvider } from 'use-query-params';
import { CLIENT_DASHBOARD_TRACING_URLS } from 'utils/datadogTracingUrls';

import { CSP } from './_document';

configureSpriteUrl(`${process.env.PATH_PREFIX || ''}/icon-sprite/sprite.svg`);
// react-remove-scroll (Radix Dialog/Popover) injects a <style> tag
// whose nonce comes from get-nonce; Turbopack has no __webpack_nonce__ fallback.
setNonce(CSP.nonce);

export const PAGES_WITHOUT_LOGIN = [
  '/login',
  '/enrollment',
  '/verify-email',
  DASHBOARD_LOGIN_PATH,
  // Reached from AppShellWithLogin's 401 handler. This shell mounts no Apollo
  // provider, so nothing here can 401 and bounce the client back out.
  ACCESS_DENIED_PATH,
];

// https://nextjs.org/docs/app/api-reference/components/font#css-variables
// Levering next/font to avoid CSP issues when downloading google fonts
const inter = Inter({
  subsets: ['latin'],
  display: 'swap',
  preload: true,
  adjustFontFallback: false,
});
const roboto = Roboto_Mono({
  subsets: ['latin'],
  display: 'swap',
  preload: true,
  adjustFontFallback: false,
});

const AppShellWithoutLogin = dynamic(
  async () => {
    const { AppShellWithoutLogin } =
      await import('components/AppShell/AppShellWithoutLogin');
    return AppShellWithoutLogin;
  },
  { ssr: false },
);

const AppShellWithLogin = dynamic(
  async () => {
    const { AppShellWithLogin } =
      await import('components/AppShell/AppShellWithLogin');
    return AppShellWithLogin;
  },
  { ssr: false },
);

function MyApp({ Component, pageProps }: AppProps) {
  // Use the layout defined at the page level, if available
  // @ts-expect-error TODO Luis use types from source/js/anchorage/apps/backoffice/src/types.ts
  const getLayout = Component.getLayout || ((page) => page);

  const isPorto = useIsPortoApp();
  usePortoTheme({
    active: isPorto,
  });

  // Pinpoints navigations to un-interpolated dynamic routes (e.g. the literal
  // "/apis/edit-role/[roleID]") at the call site — FRONTOFFICE-18A / CEXP-20068.
  useReportUninterpolatedNavigation();

  const [datadogInitialized, setDatadogInitialized] = useState(false);
  const { basePath, pathname } = useRouter();
  const AppShell = PAGES_WITHOUT_LOGIN.some((route) => pathname.includes(route))
    ? AppShellWithoutLogin
    : AppShellWithLogin;

  useEffect(() => {
    const appWindowEnv = typeof window === 'undefined' ? null : window.env;

    if (datadogInitialized || !appWindowEnv) {
      return;
    }

    const {
      DATADOG_APPLICATION_ID: applicationId,
      DATADOG_CLIENT_TOKEN: clientToken,
      DATADOG_SITE: site,
      DATADOG_SERVICE: service,
      DATADOG_ENVIRONMENT: env,
    } = appWindowEnv;

    if (applicationId && clientToken) {
      // Dynamically imported so @datadog/browser-rum stays out of the main
      // _app chunk (~80KB on login and other pages).
      import('@anchorage/datadog-rum')
        .then(({ ANCHORAGE_TRACING_URLS, initDatadogRum }) => {
          initDatadogRum({
            applicationId,
            clientToken,
            site,
            service,
            env,
            allowedTracingUrls: [
              ...ANCHORAGE_TRACING_URLS,
              ...CLIENT_DASHBOARD_TRACING_URLS,
            ],
          });
        })
        .catch(handleError);
    }

    setDatadogInitialized(true);
  }, [datadogInitialized]);

  const faviconPath = useMemo(() => {
    return `${basePath}/${isPorto ? 'portoFavicon.ico' : 'favicon.ico'}`;
  }, [isPorto, basePath]);

  useFontSetup({
    '--font-inter': inter.style.fontFamily,
    '--font-roboto-mono': roboto.style.fontFamily,
  });

  return (
    <>
      <NonceProvider nonce={CSP.nonce} cacheKey="anchor-client-dash">
        <LocalStorageProvider errorHandler={handleError}>
          <Head>
            <link id="favicon" rel="shortcut icon" href={faviconPath} />
          </Head>
          <TooltipProvider delayDuration={300} skipDelayDuration={500}>
            <ToasterProvider>
              <NuqsAdapter>
                <QueryParamProvider
                  adapter={NextAdapterPages}
                  options={{
                    enableBatching: true,
                  }}
                >
                  <LazyChunkErrorBoundary>
                    <AppShell>
                      <FadingTransition>
                        <ClientReadyGate>
                          <SchemaMismatchBanner />
                          {getLayout(<Component {...pageProps} />)}
                          <Toaster />
                        </ClientReadyGate>
                      </FadingTransition>
                    </AppShell>
                  </LazyChunkErrorBoundary>
                </QueryParamProvider>
              </NuqsAdapter>
            </ToasterProvider>
          </TooltipProvider>
        </LocalStorageProvider>
      </NonceProvider>
    </>
  );
}

export default MyApp;
